import { withSentry } from "@sentry/remix";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useParams,
  type LinksFunction,
  type LoaderFunctionArgs,
  redirect,
  type MetaFunction,
} from "react-router";
import "./app.css";
import { serverEnv } from "./lib/env.server";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { ReadableError } from "./lib/errors/ReadableError";
import { initSentry } from "./sentry";
import { browserPageEvent, browserTrackEvent } from "./lib/analytics/analyticsBrowser.events.client";
import { logDebug } from "./lib/logger";
import { getOrCreateSessionId, isNewSession } from "./lib/analytics/session-utils";
import { useDebouncedCallback } from "use-debounce";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    // Inter
    // href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
    // Inter + Source Serif 4
    // href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Source+Serif+4:ital,opsz,wght@0,8..60,200..900;1,8..60,200..900&display=swap",
    // Inter + Roboto Condensed
    // href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&display=swap",
    // Inter + Inter Tight
    href: "https://fonts.googleapis.com/css2?family=Inter+Tight:ital,wght@0,100..900;1,100..900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  // only take the public env vars (the ones that start with "PUBLIC_")
  const publicEnv = Object.fromEntries(Object.entries(serverEnv).filter(([key]) => key.startsWith("PUBLIC_")));

  return { publicEnv };
};

function App() {
  const { publicEnv } = useLoaderData<typeof loader>();
  const location = useLocation();
  const { lang = "en" } = useParams();

  useEffect(() => {
    initSentry();
  }, []);

  const pathname = location.pathname;
  const language = (lang || "en") as "en" | "fr";

  const triggerPageEvent = useDebouncedCallback(
    () => {
      logDebug("triggerPageEvent", { pathname, language });
      browserPageEvent({ language });
    },
    1000,
    { leading: true, trailing: false }
  );

  useEffect(() => {
    triggerPageEvent();
  }, [pathname, language, triggerPageEvent]);

  //
  // Track sessions
  //
  useEffect(() => {
    // Check if new session BEFORE creating one
    const newSession = isNewSession();
    const sessionId = getOrCreateSessionId();

    // Track session started if this is a new session
    if (newSession) {
      browserTrackEvent("Session Started", { sessionId });
    }
  }, []);

  //
  // GTM
  //
  // useEffect(() => {
  //   if (window.env.PUBLIC_APP_ENV !== "production") {
  //     logDebug("GTM", "Not in production");
  //     return;
  //   }

  //   (function (w: any, d: any, s: string, l: string, i: string) {
  //     w[l] = w[l] || [];
  //     w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  //     const f = d.getElementsByTagName(s)[0],
  //       j = d.createElement(s),
  //       dl = l != "dataLayer" ? "&l=" + l : "";
  //     j.async = true;
  //     j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
  //     f.parentNode.insertBefore(j, f);
  //   })(window, document, "script", "dataLayer", "GTM-MRZJLXL9");
  // }, []);

  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="odysseyregeneron.ca" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* OG */}
        <meta property="og:title" content="Odyssey Portal" />
        {/* <meta property="og:description" content="EMPAVELI.ca" /> */}
        <meta property="og:image" content="/og.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Odyssey Portal" />

        {/* make this site not indexed by search engines */}
        <meta name="robots" content="noindex, nofollow" />

        <Meta />
        <Links />

        {/**
         * This is a way to pass data from the server to the client
         */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(publicEnv || {})}`,
          }}
        />
      </head>
      <body>
        {/* gtm */}
        {/* {typeof window !== "undefined" && window.env.PUBLIC_APP_ENV === "production" && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-MRZJLXL9" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            }}
          ></noscript>
        )} */}

        <Outlet />
        <ScrollRestoration />
        <Scripts />
        <Toaster richColors />
      </body>
    </html>
  );
}

export default withSentry(App);

export function ErrorBoundary({ error }: { error: Error }) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";

  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details = error.status === 404 ? "The requested page could not be found." : error.statusText || details;
  } else if (error && error instanceof ReadableError) {
    // message = error.detail;
    details = error.detail;
  }

  if (import.meta.env.DEV && error && error instanceof Error) {
    stack = error.stack;
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="Odyssey Portal" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* OG */}
        <meta property="og:title" content="Odyssey Portal" />
        <meta property="og:description" content="Odyssey Portal" />
        <meta property="og:image" content="/og.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Odyssey Portal" />

        <Meta />
        <Links />
      </head>
      <body>
        {/* gtm */}
        {/* {typeof window !== "undefined" && window.env.PUBLIC_APP_ENV === "production" && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-MRZJLXL9" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            }}
          ></noscript>
        )} */}

        <div className="flex flex-col items-center justify-center h-screen">
          <h1 className="text-2xl font-bold">{message}</h1>
          <p className="text-sm text-gray-500">{details}</p>
        </div>
      </body>
    </html>
  );
}
